import {
  StoreData,
  getDimensionName,
  getConsiderationName,
} from '../getPageContext'

interface DimensionScore {
  name: string
  score: number
}

interface ConsiderationComparisonItem {
  name: string
  previousQuarter: number | null
  currentQuarter: number | null
}

interface CityRanking {
  name: string
  id: number
  iq: number
  starRating: number
  dimensions: DimensionScore[]
}

interface SingleCityContent {
  mode: 'single-city'
  selectedQuarter: string | null
  cityScore: {
    iq: number
    starRating: number
    dimensions: DimensionScore[]
    considerationComparison: {
      selectedDimension: string
      considerations: ConsiderationComparisonItem[]
    } | null
  }
}

interface MultiCityContent {
  mode: 'multi-city'
  selectedQuarter: string | null
  cityRankings: CityRanking[]
}

const buildSingleCityContent = (data: StoreData): SingleCityContent => {
  const { allInfo, considerationsComparison, selectedDimensionId, locale } =
    data

  const dimensions: DimensionScore[] = (
    allInfo?.LatestCityDimensions ?? []
  ).map((d) => ({
    name: getDimensionName(d.id, d.name, locale),
    score: Math.round(d.value),
  }))

  let considerationComparisonContent: SingleCityContent['cityScore']['considerationComparison'] =
    null
  if (
    considerationsComparison &&
    considerationsComparison.length > 0 &&
    selectedDimensionId
  ) {
    const dimName = getDimensionName(selectedDimensionId, undefined, locale)
    const considerations: ConsiderationComparisonItem[] =
      considerationsComparison.map((c) => ({
        name: getConsiderationName(c.id, undefined, locale),
        previousQuarter: c.prev_1 !== null ? Math.round(c.prev_1) : null,
        currentQuarter: c.curr !== null ? Math.round(c.curr) : null,
      }))

    considerationComparisonContent = {
      selectedDimension: dimName,
      considerations,
    }
  }

  return {
    mode: 'single-city',
    selectedQuarter: data.selectedQuarter,
    cityScore: {
      iq: Math.round(allInfo?.LatestCityIq ?? 0),
      starRating: allInfo?.LatestCityStar ?? 0,
      dimensions,
      considerationComparison: considerationComparisonContent,
    },
  }
}

const buildMultiCityContent = (data: StoreData): MultiCityContent => {
  const { multiCityInfos, cityCountryMap, filteredCities, locale } = data

  if (!multiCityInfos || !cityCountryMap) {
    return {
      mode: 'multi-city',
      selectedQuarter: data.selectedQuarter,
      cityRankings: [],
    }
  }

  const filteredIds =
    filteredCities && filteredCities.length > 0
      ? new Set(filteredCities.map((c) => c.id))
      : null

  const rankings: CityRanking[] = Object.values(multiCityInfos)
    .filter((city) => {
      if (!city.iq) return false
      if (filteredIds && !filteredIds.has(city.city_id)) return false
      return true
    })
    .map((city) => {
      const cityMeta = cityCountryMap.obj[String(city.city_id)]
      const dimensions: DimensionScore[] = city.dimensions.map((d) => ({
        name: getDimensionName(d.id, d.name, locale),
        score: Math.round(d.value),
      }))

      return {
        name: cityMeta.name,
        id: city.city_id,
        iq: Math.round(city.iq),
        starRating: city.iq_quality_star,
        dimensions,
      }
    })
    .sort((a, b) => b.iq - a.iq)

  return {
    mode: 'multi-city',
    selectedQuarter: data.selectedQuarter,
    cityRankings: rankings,
  }
}

export const buildDashboardContent = (
  data?: StoreData
): Record<string, unknown> => {
  if (!data) return {}

  if (data.isMultiCity) {
    return buildMultiCityContent(data) as unknown as Record<string, unknown>
  }
  return buildSingleCityContent(data) as unknown as Record<string, unknown>
}
